<template>
  <div class="blog-list">
    <div v-if="loading && posts.length === 0" class="loading">Loading blog posts...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="posts.length === 0" class="empty">No blog posts available yet.</div>
    <div v-else>
      <ul>
        <router-link
          v-for="(post, index) in posts"
          :key="post.id"
          :to="'/blog/' + post.id"
        >
          <li :class="getBackgroundClass(index)">
            <h2>{{ post.title }}</h2>
            <p>{{ post.date }}</p>
          </li>
        </router-link>
      </ul>
      
      <div v-if="hasMorePosts || loading" class="load-more-container">
        <button 
          v-if="hasMorePosts && !loading" 
          @click="loadMorePosts" 
          class="load-more-btn"
        >
          Load More Posts
        </button>
        <div v-if="loading && posts.length > 0" class="loading-more">Loading more posts...</div>
      </div>
      
      <div v-if="!hasMorePosts && posts.length > 0" class="end-message">
        You've reached the end of the posts!
      </div>
    </div>
  </div>
</template>

<script>
import { useBlog } from '@/services/blog';

export default {
  name: 'BlogList',
  data() {
    return {
      posts: [],
      loading: true,
      error: null,
      currentPage: 1,
      postsPerPage: 20,
      totalPosts: 0,
      hasMorePosts: false
    };
  },
  methods: {
    async fetchPosts(page = 1) {
      try {
        this.loading = true;
        const { getPublicPosts } = useBlog();
        const offset = (page - 1) * this.postsPerPage;
        const response = await getPublicPosts(this.postsPerPage, offset);
        
        // Map the posts to the expected format (optimized response already contains minimal data)
        const mappedPosts = response.posts.map(post => ({
          id: post.slug, // Use slug for routing
          title: post.title,
          date: new Date(post.publish_date).toLocaleDateString()
        }));

        if (page === 1) {
          this.posts = mappedPosts;
        } else {
          this.posts = [...this.posts, ...mappedPosts];
        }

        this.currentPage = page;
        this.totalPosts = response.total;
        this.hasMorePosts = (page * this.postsPerPage) < response.total;
      } catch (error) {
        console.error('Error fetching posts:', error);
        this.error = 'Failed to load blog posts.';
      } finally {
        this.loading = false;
      }
    },

    async loadMorePosts() {
      if (!this.hasMorePosts || this.loading) return;
      await this.fetchPosts(this.currentPage + 1);
    },
    getBackgroundClass(index) {
      return index % 2 === 0 ? 'bg-white' : 'bg-light-gray';
    }
  },
  created() {
    this.fetchPosts();
  }
};
</script>

<style scoped>
.blog-list {
  border-radius: 8px;
  border: 1px solid #ddd;
  background-color: #fff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  max-width: 800px;
  margin: 0 auto 2rem;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  background-color: #f9f9f9;
}

.blog-list li:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.blog-list h2 {
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
  color: #333;
}

.blog-list p {
  font-size: 1rem;
  color: #666;
}

.blog-list ul {
  list-style: none;
  padding: 0;
}

.blog-list li {
  padding: 1rem;
  border-bottom: 1px solid #ddd;
}

.blog-list li:last-child {
  border-bottom: none;
}

.blog-list a {
  color: #007BFF;
  text-decoration: none;
  font-weight: 600;
}

.bg-white {
  background: #fff;
}

.bg-light-gray {
  background: #f4f4f4;
}

.loading, .error, .empty {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.error {
  color: #dc2626;
}

.load-more-container {
  text-align: center;
  margin-top: 2rem;
  padding: 1rem;
}

.load-more-btn {
  background: linear-gradient(135deg, #007BFF 0%, #0056b3 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
}

.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
}

.load-more-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.loading-more {
  color: #666;
  font-style: italic;
  padding: 1rem;
}

.end-message {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
  border-top: 1px solid #ddd;
  margin-top: 2rem;
}
</style>